import { useEffect, useMemo, useState } from "react";
import { MessageCircle, Eye, Phone, Mail, StickyNote, Search, CarFront, CreditCard, Wrench } from "lucide-react";
import api, { errMsg, fmtUSD, fmtDate, STATUS_LABELS, STATUS_COLORS, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const COMM_TYPES = { call: "Llamada", whatsapp: "WhatsApp", email: "Email", note: "Nota interna" };
const COMM_ICONS = { call: Phone, whatsapp: MessageCircle, email: Mail, note: StickyNote };
const SERVICE_LABELS = { repair: "Reparación", shipping: "Envío", export: "Exportación" };

export default function AdminCRM({ leadsOnly = false }) {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [comm, setComm] = useState({ type: "call", message: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const open = async (userId) => {
    try {
      const { data } = await api.get(`/admin/customers/${userId}`);
      setDetail(data);
      setComm({ type: "call", message: "" });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const addComm = async () => {
    if (!comm.message.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/customers/${detail.user.id}/communications`, comm);
      toast.success("Comunicación registrada");
      setComm({ type: "call", message: "" });
      open(detail.user.id);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const filtered = useMemo(() => {
    let list = customers;
    if (leadsOnly) list = list.filter((c) => !c.has_paid_deposit);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((c) => (c.name || "").toLowerCase().includes(q) || (c.email || "").includes(q));
    }
    return list;
  }, [customers, search, leadsOnly]);

  return (
    <div data-testid={leadsOnly ? "crm-leads" : "crm-customers"}>
      <div className="flex items-center gap-3 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
          <Input data-testid="crm-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o correo" className="pl-9 bg-[#0B0F1A] border-white/15 w-72 h-9" />
        </div>
        <span className="text-xs text-slate-500">
          {filtered.length} {leadsOnly ? "leads sin depósito pagado" : "clientes"}
        </span>
      </div>

      <div className="card-tech rounded-xl overflow-x-auto">
        <table className="w-full min-w-[820px]" data-testid="crm-table">
          <thead>
            <tr className="border-b border-white/10 text-left text-xs text-slate-500 uppercase tracking-wider">
              <th className="py-3 px-4">Cliente</th><th className="py-3 px-4">Contacto</th>
              <th className="py-3 px-4">Solicitudes</th><th className="py-3 px-4">Depositado</th>
              <th className="py-3 px-4">Servicios</th><th className="py-3 px-4">Última actividad</th>
              <th className="py-3 px-4">ID</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors cursor-pointer"
                onClick={() => open(c.id)} data-testid={`crm-row-${c.id}`}>
                <td className="py-3 px-4">
                  <p className="font-medium text-slate-200 text-sm">{c.name || "—"}</p>
                  {!c.has_paid_deposit && (
                    <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/40 border text-[10px] mt-1">Lead</Badge>
                  )}
                </td>
                <td className="py-3 px-4">
                  <p className="text-sm text-slate-300">{c.email}</p>
                  <p className="text-xs text-slate-500">{c.phone || "sin teléfono"}</p>
                </td>
                <td className="py-3 px-4 text-sm text-slate-300">{c.bids_count}</td>
                <td className="py-3 px-4 text-sm text-slate-200 font-medium">{fmtUSD(c.deposits_paid)}</td>
                <td className="py-3 px-4 text-sm text-slate-300">{c.services_count}</td>
                <td className="py-3 px-4 text-sm text-slate-400">{fmtDate(c.last_activity)}</td>
                <td className="py-3 px-4">
                  {c.has_id_document
                    ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40 border text-xs">Subido</Badge>
                    : <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/40 border text-xs">Pendiente</Badge>}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="py-10 text-center text-sm text-slate-500">
                {leadsOnly ? "Sin leads por ahora" : "Sin clientes registrados"}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Ficha de cliente 360° */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#111827] border-white/10 text-slate-50 max-w-3xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle data-testid="crm-detail-title">{detail.user.name || detail.user.email}</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 pt-2">
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <span className="text-slate-400">{detail.user.email}</span>
                  {detail.user.phone && <span className="text-slate-400">· {detail.user.phone}</span>}
                  {detail.user.has_id_document && (
                    <a href={`${API}/users/${detail.user.id}/id-document`} target="_blank" rel="noreferrer"
                      data-testid="crm-view-id" className="text-[#2563EB] hover:text-blue-400 inline-flex items-center gap-1">
                      <Eye className="w-3.5 h-3.5" strokeWidth={1.5} /> Ver ID
                    </a>
                  )}
                  {detail.user.phone && (
                    <a href={`https://wa.me/${detail.user.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" data-testid="crm-wa">
                      <Button size="sm" variant="outline" className="border-emerald-500/50 text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-full h-8">
                        <MessageCircle className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> WhatsApp
                      </Button>
                    </a>
                  )}
                </div>

                {/* Solicitudes */}
                <div className="border border-white/10 rounded-lg p-5">
                  <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <CarFront className="w-4 h-4 text-[#2563EB]" strokeWidth={1.5} /> Solicitudes ({detail.bids.length})
                  </p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {detail.bids.map((b) => {
                      const lot = b.lot || {};
                      const vehicle = `${lot.year || ""} ${lot.make || ""} ${lot.model || ""}`.trim() || `Lote ${lot.lot_number || "—"}`;
                      return (
                        <div key={b.id} className="flex items-center justify-between gap-3 text-sm">
                          <span className="text-slate-300 truncate">{vehicle} · {fmtUSD(b.max_bid)}</span>
                          <Badge className={`${STATUS_COLORS[b.status]} border text-xs shrink-0`}>{STATUS_LABELS[b.status]}</Badge>
                        </div>
                      );
                    })}
                    {detail.bids.length === 0 && <p className="text-xs text-slate-500">Sin solicitudes todavía</p>}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  {/* Pagos */}
                  <div className="border border-white/10 rounded-lg p-5">
                    <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-[#22C55E]" strokeWidth={1.5} /> Pagos ({detail.payments.length})
                    </p>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {detail.payments.map((p) => (
                        <div key={p.session_id} className="flex items-center justify-between text-sm">
                          <span className="text-slate-400 text-xs">{fmtDate(p.created_at)}</span>
                          <span className="text-slate-200">{fmtUSD(p.amount)}</span>
                          <Badge className={`border text-[10px] ${p.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : p.payment_status === "refunded" ? "bg-slate-500/20 text-slate-300 border-slate-400/40" : "bg-amber-500/15 text-amber-300 border-amber-500/40"}`}>
                            {p.payment_status}
                          </Badge>
                        </div>
                      ))}
                      {detail.payments.length === 0 && <p className="text-xs text-slate-500">Sin pagos</p>}
                    </div>
                  </div>
                  {/* Servicios */}
                  <div className="border border-white/10 rounded-lg p-5">
                    <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Wrench className="w-4 h-4 text-amber-400" strokeWidth={1.5} /> Servicios ({detail.services.length})
                    </p>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {detail.services.map((s) => (
                        <div key={s.id} className="text-sm">
                          <span className="text-slate-300">{SERVICE_LABELS[s.type] || s.type}</span>
                          <span className="text-xs text-slate-500"> · {fmtDate(s.created_at)} · {s.status}</span>
                        </div>
                      ))}
                      {detail.services.length === 0 && <p className="text-xs text-slate-500">Sin servicios</p>}
                    </div>
                  </div>
                </div>

                {/* Comunicaciones */}
                <div className="border border-white/10 rounded-lg p-5" data-testid="crm-communications">
                  <p className="text-sm font-semibold mb-4">Registro de comunicaciones</p>
                  <div className="flex flex-wrap gap-3 mb-5">
                    <Select value={comm.type} onValueChange={(v) => setComm({ ...comm, type: v })}>
                      <SelectTrigger data-testid="crm-comm-type" className="w-40 bg-[#0B0F1A] border-white/15 h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(COMM_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Textarea data-testid="crm-comm-message" value={comm.message}
                      onChange={(e) => setComm({ ...comm, message: e.target.value })}
                      placeholder="Ej. Llamé al cliente, confirmó que enviará el wire mañana"
                      className="flex-1 min-w-[220px] bg-[#0B0F1A] border-white/15 min-h-[38px] h-[38px]" />
                    <Button data-testid="crm-comm-add" onClick={addComm} disabled={busy || !comm.message.trim()}
                      className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full h-9">
                      Registrar
                    </Button>
                  </div>
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {detail.communications.map((c, i) => {
                      const Icon = COMM_ICONS[c.type] || StickyNote;
                      return (
                        <div key={c.id} className="flex gap-3 text-sm fade-up" style={{ animationDelay: `${i * 40}ms` }}>
                          <Icon className="w-4 h-4 text-[#2563EB] mt-0.5 shrink-0" strokeWidth={1.5} />
                          <div>
                            <p className="text-slate-200">{c.message}</p>
                            <p className="text-xs text-slate-500">{COMM_TYPES[c.type]} · {fmtDate(c.at)} · {c.by}</p>
                          </div>
                        </div>
                      );
                    })}
                    {detail.communications.length === 0 && (
                      <p className="text-xs text-slate-500">Sin comunicaciones registradas</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
